import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import cookie from "js-cookie";
import { Icon, Form } from "semantic-ui-react";
import styled from "styled-components";
import { getTheme } from "Theme";
import { getButtonColor } from "Theme";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "store/slices/userSlice";
import { addNotification } from "../../store/slices/uiSlice";
import { notifTags } from "globalComponents/Notification";
import Timer from "globalComponents/Timer";

const theme = getTheme();

const Main = styled.div`
  display: flex;
  flex-direction: column;
  background: ${theme.SECONDARY_COLOR};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
`;

const Error = styled.h1`
  color: ${theme.NEGATIVE};
  font-size: 14px;
  font-weight: 500;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  gap: 20px;
  padding: 10px;
  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
  color: white;
  padding: 8px;
  border-bottom: 2px solid ${theme.BLACK};
  background: ${theme.MAIN_COLOR};
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  margin: 0;
`;

const Button = styled.button`
  border: none;
  outline: none;
  background: ${(props) => getButtonColor(props.color)};
  color: ${theme.BUTTON_TEXT};
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
  padding: 10px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => props.disabled && 0.7};
  :hover {
    opacity: 0.8;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin: 10px auto;
`;

const TimerManager = () => {
  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const [positions, setPositions] = useState([]);
  const [clientTimer, setClientTimer] = useState({});

  const dispatch = useDispatch();
  const user = useSelector((state) => state.current.user);
  const [duration, setDuration] = useState(0);

  const startTimer = () => {
    if (!position) setError("Select a position");
    else
      axios
        .post(`${process.env.REACT_APP_API_URL}/user/startTimer`, {
          start: moment().utc(),
          positionId: position._id,
          id: user._id || cookie.get("id"),
        })
        .then((res) => {
          dispatch(
            addNotification({
              type: notifTags.success,
              message: "Clocked In!",
            })
          );

          dispatch(
            setUser({
              user: {
                ...res.data.user,
                currentTimer: {
                  start: res.data.user.currentTimer.start,
                  position: position,
                },
              },
            })
          );
        })
        .catch((err) => {
          dispatch(
            addNotification({
              type: notifTags.error,
              message: "Clock in failed!",
            })
          );
        });
  };

  const endTimer = () => {
    axios
      .post(`${process.env.REACT_APP_API_URL}/user/endTimer`, {
        start: moment(user.currentTimer.start).utc(),
        end: moment().utc(),
        id: user._id,
      })
      .then((res) => {
        dispatch(setUser({ user: res.data.user }));
        dispatch(
          addNotification({
            type: notifTags.success,
            message: "Clocked out!",
          })
        );
      })
      .catch((err) => {
        dispatch(
          addNotification({
            type: notifTags.error,
            message: "Clock out failed!",
          })
        );
      });
  };

  const cancelTimer = () => {
    axios
      .post(`${process.env.REACT_APP_API_URL}/user/cancelTimer`, {
        id: user._id,
      })
      .then((res) => {
        dispatch(setUser({ user: res.data.user }));
        dispatch(
          addNotification({
            type: notifTags.success,
            message: "Timer Cancelled",
          })
        );
      })
      .catch((err) => {
        dispatch(
          addNotification({
            type: notifTags.error,
            message: "Failed to cancel timer",
          })
        );
      });
  };

  const fetchPositions = () => {
    axios
      .get("/positions", {
        params: { userId: user._id },
      })
      .then(async (res) => {
        if (!res.data || res.data.length < 1) {
          setPositions([]);
        } else {
          setPositions(
            res.data.map((p) => ({
              text: `${p.client.name} (${p.name})`,
              value: p,
            }))
          );
          setError(null);
        }
      });
  };
  useEffect(() => {
    fetchPositions();
    if (user.currentTimer) {
      if (user.currentTimer.position.client)
        setClientTimer(user.currentTimer.position.client);
      setDuration(
        moment.duration(
          moment
            .tz(clientTimer.timezone)
            .diff(moment.tz(user.currentTimer.start, clientTimer.timezone))
        )
      );
    }
  }, [user]);

  useEffect(() => {
    setTimeout(
      () =>
        user.currentTimer &&
        setDuration(
          moment.duration(
            moment
              .tz(clientTimer.timezone)
              .diff(moment.tz(user.currentTimer.start, clientTimer.timezone))
          )
        ),
      1000
    );
  }, [duration]);

  return (
    <Main>
      <Header as="h1" style={{ textAlign: "center", fontSize: "24px" }}>
        Your Timecard
      </Header>
      {user.smsClockinDisabled == false && (
        <>
          {!user.currentTimer || !clientTimer.timezone ? (
            <FormContainer>
              <Form.Dropdown
                onChange={(e, v) => {
                  setPosition(v.value);
                }}
                selection
                search
                options={positions.filter((opt) => opt.text !== "All")}
                placeholder={
                  !user._id ? "Select a User First" : "Select Client"
                }
                value={position}
                style={{ width: "100%" }}
                name="timer-client"
                disabled={!user._id}
              />
              <Button color="blue" onClick={startTimer} disabled={!position}>
                <Icon name="clock" />
                Clock In
              </Button>
            </FormContainer>
          ) : (
            <Timer
              start={user.currentTimer.start}
              timezone={clientTimer.timezone}
              clientName={clientTimer.name}
              positionName={user.currentTimer.position.name}
            />
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              margin: "auto",
              width: "auto",
            }}
          >
            {user.currentTimer && (
              <ButtonRow>
                <Button color="blue" onClick={endTimer}>
                  <Icon name="clock" />
                  Clock Out
                </Button>
                <Button color="grey" onClick={cancelTimer}>
                  Cancel Timer
                </Button>
              </ButtonRow>
            )}
          </div>
        </>
      )}

      {error && <Error>{error}</Error>}
    </Main>
  );
};

export default TimerManager;
